/**
 * スナップショット管理ユーティリティ
 * デモスナップショットの作成・削除・再作成を管理
 */

import { demoResetService } from '../services/demoResetService';
import logger from './logger';

const DEMO_COMPANY_ID = 'e32a0499-6456-44ac-9b16-b0001098c259';

export class SnapshotManager {
  /**
   * 全スナップショットをクリーンアップ
   */
  static async cleanupAllSnapshots(): Promise<void> {
    try {
      logger.info('[SnapshotManager] 全スナップショットをクリーンアップ中...');

      demoResetService.setCompanyId(DEMO_COMPANY_ID);

      const cleanupResult = await demoResetService.cleanupSnapshots(0); // 0時間 = 全削除

      if (cleanupResult.success) {
        logger.info('[SnapshotManager] クリーンアップ完了:', cleanupResult);
      } else {
        logger.error('[SnapshotManager] クリーンアップ失敗:', cleanupResult.message);
      }

      // return cleanupResult;
    } catch (error) {
      logger.error('[SnapshotManager] クリーンアップエラー:', error);
      throw error;
    }
  }

  /**
   * 新しいベースラインスナップショットを作成
   */
  static async createBaselineSnapshot(): Promise<void> {
    try {
      logger.info('[SnapshotManager] 新しいベースラインスナップショット作成中...');

      demoResetService.setCompanyId(DEMO_COMPANY_ID);

      const snapshotResult = await demoResetService.createSnapshot({
        onProgress: (message, step, total) => {
          logger.info(`[SnapshotManager] ${message} (${step}/${total})`);
        },
      });

      if (snapshotResult.success) {
        logger.info('[SnapshotManager] ベースラインスナップショット作成完了:', snapshotResult);
      } else {
        logger.error('[SnapshotManager] スナップショット作成失敗:', snapshotResult.message);
      }

      // return snapshotResult;
    } catch (error) {
      logger.error('[SnapshotManager] スナップショット作成エラー:', error);
      throw error;
    }
  }

  /**
   * 実際のテーブルから不要なデモデータを削除
   */
  static async deleteActualDemoData(partnerIdsToDelete: string[]): Promise<void> {
    try {
      logger.info('[SnapshotManager] 実際のデモデータ削除開始...', partnerIdsToDelete);

      const { getSupabaseClient } = await import('./supabase');
      const supabase = getSupabaseClient();

      if (!supabase) {
        throw new Error('Supabaseクライアントが初期化されていません');
      }

      // Step 1: business_partner_locations から削除
      const { error: locationsError } = await supabase
        .from('business_partner_locations')
        .delete()
        .in('business_partner_id', partnerIdsToDelete);

      if (locationsError) {
        logger.error('[SnapshotManager] locations削除エラー:', locationsError);
        throw new Error(`関連場所データの削除に失敗: ${locationsError.message}`);
      }

      // Step 2: business_partners から削除
      const { error: partnersError } = await supabase
        .from('business_partners')
        .delete()
        .in('id', partnerIdsToDelete)
        .eq('company_id', DEMO_COMPANY_ID);

      if (partnersError) {
        logger.error('[SnapshotManager] partners削除エラー:', partnersError);
        throw new Error(`取引先データの削除に失敗: ${partnersError.message}`);
      }

      logger.info('[SnapshotManager] 実際のデモデータ削除完了:', partnerIdsToDelete.length, '件');
    } catch (error) {
      logger.error('[SnapshotManager] 実際のデータ削除エラー:', error);
      throw error;
    }
  }

  /**
   * 完全なスナップショットリセット（クリーンアップ + 新規作成）
   */
  static async resetSnapshot(): Promise<void> {
    try {
      logger.info('[SnapshotManager] スナップショット完全リセット開始...');

      // Step 1: 古いスナップショットを削除
      await this.cleanupAllSnapshots();

      // Step 2: 新しいベースラインを作成
      await this.createBaselineSnapshot();

      logger.info('[SnapshotManager] スナップショット完全リセット完了');
    } catch (error) {
      logger.error('[SnapshotManager] スナップショットリセットエラー:', error);
      throw error;
    }
  }

  /**
   * 完全なデモ環境リセット（実データ削除 + スナップショット再作成）
   */
  static async resetDemoEnvironment(demoDataIds: string[]): Promise<void> {
    try {
      logger.info('[SnapshotManager] デモ環境完全リセット開始...');

      // Step 1: 実際のテーブルから不要なデモデータを削除
      if (demoDataIds.length > 0) {
        await this.deleteActualDemoData(demoDataIds);
      }

      // Step 2: スナップショット再作成
      await this.resetSnapshot();

      logger.info('[SnapshotManager] デモ環境完全リセット完了');
    } catch (error) {
      logger.error('[SnapshotManager] デモ環境リセットエラー:', error);
      throw error;
    }
  }

  /**
   * 重複バックアップデータのクリーンアップ
   */
  static async cleanupDuplicateBackups(dryRun: boolean = true): Promise<void> {
    try {
      logger.info(
        '[SnapshotManager] 重複バックアップデータクリーンアップ開始... (dryRun:',
        dryRun,
        ')'
      );

      demoResetService.setCompanyId(DEMO_COMPANY_ID);

      const result = await demoResetService.cleanupDuplicateBackups(dryRun);

      if (result.success) {
        logger.info('[SnapshotManager] クリーンアップ結果:', result);

        if (dryRun) {
          const totalDuplicates = Object.values(result.duplicatesFound).reduce(
            (sum, info: any) => sum + info.duplicatesToDelete,
            0
          );
          logger.info(
            `[SnapshotManager] ドライラン完了: ${totalDuplicates} 件の重複データが見つかりました`
          );
        } else {
          const totalDeleted = Object.values(result.deletedRecords || {}).reduce(
            (sum, count: any) => sum + count,
            0
          );
          logger.info(
            `[SnapshotManager] クリーンアップ完了: ${totalDeleted} 件の重複データを削除しました`
          );
        }
      } else {
        logger.error('[SnapshotManager] クリーンアップ失敗:', result.message);
      }

      // return result;
    } catch (error) {
      logger.error('[SnapshotManager] クリーンアップエラー:', error);
      throw error;
    }
  }
}

// デバッグ用のエクスポート
export const debugSnapshotManager = {
  cleanup: () => SnapshotManager.cleanupAllSnapshots(),
  create: () => SnapshotManager.createBaselineSnapshot(),
  reset: () => SnapshotManager.resetSnapshot(),
  deleteActualData: (ids: string[]) => SnapshotManager.deleteActualDemoData(ids),
  resetEnvironment: (ids: string[]) => SnapshotManager.resetDemoEnvironment(ids),
  cleanupDuplicates: (dryRun: boolean = true) => SnapshotManager.cleanupDuplicateBackups(dryRun),
};

// グローバルに公開（デバッグ用）
if (typeof window !== 'undefined') {
  (window as any).snapshotManager = debugSnapshotManager;
}
